import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "../../Assets/svgs/AddIcon";
import CallMobileIcon from "../../Assets/svgs/CallMobileIcon";
import PhoneBgIcon from "../../Assets/svgs/PhoneBgIcon";
import SolidTrashIcon from "../../Assets/svgs/SolidTrashIcon";
import { Loader } from "../../Common/Loader";
import {
  getPhoneNumberLimits,
  handlePhoneNoChange,
  phoneNumberValidation,
} from "../../Common/Validations";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { ErrorModel } from "../../Model/ErrorModel";
import ImportPhoneNumberModal from "../../Model/ImportPhoneNumberModal";
import { SuccessModel } from "../../Model/SuccessModel";
import { getAssistantList } from "../../Redux/Slice/Assistant/AssistantsListSlice";
import { setUnsavedChanges } from "../../Redux/Slice/Common/SelectedTabSlice";
import { deletePhoneNumber } from "../../Redux/Slice/PhoneNumbers/DeletePhoneNumberSlice";
import { getPhoneNumberDetail } from "../../Redux/Slice/PhoneNumbers/GetPhoneNumberDetailSlice";
import { getPhoneNumbers } from "../../Redux/Slice/PhoneNumbers/GetPhoneNumberListSlice";
import { updatePhoneNumber } from "../../Redux/Slice/PhoneNumbers/UpdatePhoneNumberSlice";
import { PhoneListSidebar } from "./PhoneListSidebar";
import { ALL_CALLING_CODES } from "../../Common/Constants";
import {
  getCountryCodeByFullPhone,
  maskNoByCallingCode,
} from "../../Common/utils";
import CountryPhoneInput from "../../Common/CountryPhoneInput";

const AddPhoneNumber = () => {
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    fallbackDestinationCountryCode: "+61",
    fallbackDestinationPhoneNumber: "",
    assistantId: "",
    phoneNumberId: "",
    isPhoneNumberDefault: false,
    isAddInboundSettings: false,
  });
  const [formError, setFormError] = useState({
    fallbackDestinationPhoneNumberError: "",
  });
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedModel, setHasChangedModel] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const AssistantList = useSelector((state) => state.assistants.assistantsList);
  const { phoneNoListData, loading } = useSelector(
    (state) => state?.phoneNoList
  );
  const phoneNoList =
    phoneNoListData?.data && Array.isArray(phoneNoListData?.data)
      ? phoneNoListData?.data
      : [];

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;
    if (name == "fallbackDestinationPhoneNumber") {
      handlePhoneNoChange(
        e,
        formData.fallbackDestinationCountryCode,
        () =>
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          })),
        (newError) =>
          setFormError({
            ...formError,
            fallbackDestinationPhoneNumberError: newError,
          }),
        (isChanged) => {
          setHasChanged(isChanged);
          dispatch(setUnsavedChanges(true));
        },
        false
      );
    } else if (name == "fallbackDestinationCountryCode") {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      const phoneNo = formData.fallbackDestinationPhoneNumber;
      let formatted = phoneNo;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        fallbackDestinationPhoneNumber: formatted,
      }));
      const minDigits = getPhoneNumberLimits(value)?.min ?? 10;
      const rawDigits = phoneNo?.replace(/\D/g, "");
      const isValid = phoneNumberValidation(rawDigits, value);
      setFormError({
        ...formError,
        fallbackDestinationPhoneNumberError:
          isValid || !value || !phoneNo
            ? ""
            : rawDigits.length < minDigits
            ? `Phone number must be at least ${minDigits} digits long.`
            : "Please enter a valid phone number",
      });
    } else {
      setHasChanged(true);
      dispatch(setUnsavedChanges(true));
      setFormData((prev) => ({
        ...prev,
        [name]: type == "checkbox" ? checked : value,
      }));
    }
  };

  const refetchPhoneNoData = async () => {
    await dispatch(getPhoneNumbers());
    await getPhoneNumberDetails(selectedPhoneNumber?.id);
  };

  const onDelete = async () => {
    setConfirmModal(false);
    setIsLoading(true);
    const result = await dispatch(deletePhoneNumber(selectedPhoneNumber?.id));
    setIsLoading(false);
    if (
      deletePhoneNumber.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Phone Number Deleted Successfully!", () =>
        getPhoneNumberData()
      );
    } else {
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const updatePhoneNumberData = async () => {
    setIsLoading(true);
    const payload = {
      phoneNumber: selectedPhoneNumber?.id,
      body: {
        name: selectedPhoneNumber?.number,
        isPhoneNumberDefault: formData.isPhoneNumberDefault,
        assistantId: formData.isAddInboundSettings
          ? formData.assistantId
          : null,
        fallbackDestination:
          formData.fallbackDestinationPhoneNumber &&
          formData.isAddInboundSettings
            ? {
                type: "number",
                number: `${
                  formData.fallbackDestinationCountryCode
                }${formData.fallbackDestinationPhoneNumber?.replace(
                  /\D/g,
                  ""
                )}`,
              }
            : null,
      },
    };
    const result = await dispatch(updatePhoneNumber(payload));
    setIsLoading(false);
    if (
      updatePhoneNumber.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Phone Number Details Updated Successfully!", () =>
        refetchPhoneNoData()
      );
    } else {
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const getPhoneNumberDetails = async (id) => {
    setIsLoading(true);
    try {
      const result = await dispatch(getPhoneNumberDetail(id));
      if (
        getPhoneNumberDetail.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setHasChanged(false);
        dispatch(setUnsavedChanges(false));
        const phoneNoDetails = result?.payload?.data;
        const fallbackCountryCode = phoneNoDetails?.fallbackDestination?.number
          ? getCountryCodeByFullPhone(
              phoneNoDetails?.fallbackDestination?.number
            )
          : "+1";

        const fallbackPhoneNo = phoneNoDetails?.fallbackDestination?.number
          ? phoneNoDetails?.fallbackDestination?.number?.replace(
              fallbackCountryCode,
              ""
            )
          : "";
        if (phoneNoDetails) {
          setFormData((prev) => ({
            ...prev,
            fallbackDestinationCountryCode: fallbackCountryCode,
            fallbackDestinationPhoneNumber: fallbackPhoneNo,
            assistantId: phoneNoDetails?.assistantId ?? "",
            phoneNumberId: phoneNoDetails?.id,
            isPhoneNumberDefault: phoneNoDetails?.isPhoneNumberDefault ?? false,
            isAddInboundSettings: phoneNoDetails?.assistantId ? true : false,
          }));
          setSelectedPhoneNumber(phoneNoDetails);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getPhoneNumberData = async () => {
    const result = await dispatch(getPhoneNumbers());
    if (
      getPhoneNumbers.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      const phoneNumbersList =
        result?.payload?.data && Array.isArray(result?.payload?.data)
          ? result?.payload?.data
          : [];
      if (phoneNumbersList?.length) {
        getPhoneNumberDetails(phoneNumbersList[0]?.id);
      }
    }
  };

  const changeSelection = (phoneNo) => {
    getPhoneNumberDetails(phoneNo?.id);
  };

  useEffect(() => {
    getPhoneNumberData();
    dispatch(getAssistantList({ text: "", isDropdown: true }));
  }, []);

  return (
    <div className="page__body">
      {(isLoading || loading) && <Loader />}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {importModalVisible ? (
        <ImportPhoneNumberModal
          closePopup={() => setImportModalVisible(false)}
          onSuccess={() => {
            setImportModalVisible(false);
            showSuccessMessage("Phone number imported successfully!");
            getPhoneNumberData();
          }}
        />
      ) : null}
      {confirmModal ? (
        <ConfirmModel
          onAccept={onDelete}
          onReject={() => {
            setConfirmModal(false);
          }}
          title="Delete Phone Number!"
          message="Are you sure you want to Delete the Phone Number?"
        />
      ) : (
        ""
      )}
      {hasChangedModel ? (
        <ConfirmModel
          onAccept={() => {
            dispatch(setUnsavedChanges(false));
          }}
          yes={"Yes"}
          no={"No"}
          onReject={() => {
            setHasChangedModel(false);
          }}
          title="Update Changes!"
          message="Changes made will not be saved. Are you sure you want to leave the page?"
        />
      ) : null}
      {phoneNoList?.length && selectedPhoneNumber ? (
        <div className="createAssistantMain h_100">
          <PhoneListSidebar
            selectedPhoneNumber={selectedPhoneNumber}
            changeSelection={changeSelection}
            showSuccessMessage={showSuccessMessage}
            getPhoneNumberData={getPhoneNumberData}
          />
          <div className="createAssistantMain__col h_100">
            <div className="createAssistantMain__card">
              <div className="createAssistantHeader createAssistantHeader--flexStart">
                <div
                  className="createAssistantHeader__info"
                  style={{ width: "80%" }}
                >
                  <div className="title">
                    <div className="icon">
                      <PhoneBgIcon />
                    </div>
                    <div className="createAssistantHeader__info__content">
                      <h1
                        className="title title--h3"
                        style={{ display: "inline" }}
                      >
                        Inbound settings
                        <br />
                        <p>{selectedPhoneNumber?.number}</p>
                      </h1>
                    </div>
                  </div>
                  <p className="para para--note">
                    Assistants are able to be connected to phone numbers for
                    calls. You can import from Twilio, vonage, or create a free
                    number directly from Vapi for use with your assistants.
                  </p>
                </div>
                <div className="btn__group">
                  <button
                    className="btn btn__primary"
                    disabled={
                      !hasChanged || formData.isAddInboundSettings
                        ? !formData.assistantId ||
                          formError.fallbackDestinationPhoneNumberError
                        : false
                    }
                    onClick={updatePhoneNumberData}
                  >
                    Submit
                  </button>
                  <button
                    className="btn btn__danger btn__outlined"
                    onClick={() => setConfirmModal(true)}
                  >
                    <SolidTrashIcon />
                    Delete
                  </button>
                </div>
              </div>
            </div>
            <div className="createAssistantMain__card createAssistantMain__card--second">
              <div className="createAssistantBody">
                <div className="formRow">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <div className="form__checkbox__option m_0">
                        <input
                          type="checkbox"
                          id="isAddInboundSettings"
                          className="form__checkbox__input"
                          value={formData?.isAddInboundSettings}
                          name="isAddInboundSettings"
                          checked={formData?.isAddInboundSettings ?? false}
                          onChange={handleChange}
                        />
                        <label
                          for="isAddInboundSettings"
                          className="form__checkbox__label"
                        >
                          Add Inbound settings
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                {formData.isAddInboundSettings ? (
                  <>
                    <div className="formCol">
                      <div className="form__Field form__Field--full">
                        <label for="assistantId" className="form__label">
                          Assistant
                          <sup className="form__label__asterisk">*</sup>
                        </label>
                        <select
                          className="form__select form__select--sm"
                          id="assistantId"
                          name="assistantId"
                          value={formData.assistantId}
                          onChange={handleChange}
                        >
                          <option selected="" value="" disabled>
                            -Select-
                          </option>
                          {AssistantList?.data &&
                          Array.isArray(AssistantList?.data)
                            ? AssistantList?.data.map((assistant) => (
                                <option value={assistant.id}>
                                  {assistant.name}
                                </option>
                              ))
                            : ""}
                        </select>
                      </div>
                      <p className="para para--right">
                        <div className="form__checkbox__option m_0">
                          <input
                            type="checkbox"
                            id="isPhoneNumberDefault"
                            className="form__checkbox__input"
                            value={formData?.isPhoneNumberDefault}
                            name="isPhoneNumberDefault"
                            checked={formData?.isPhoneNumberDefault ?? false}
                            onChange={handleChange}
                          />
                          <label
                            for="isDefaultEmail"
                            className="form__checkbox__label"
                          >
                            Make this phone number as default
                          </label>
                        </div>
                      </p>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label for="phonenumber" className="form__label">
                          Fallback Destination
                        </label>
                        <CountryPhoneInput
                          countryCodeFieldName="fallbackDestinationCountryCode"
                          phoneFieldName="fallbackDestinationPhoneNumber"
                          countryCode={formData.fallbackDestinationCountryCode}
                          phoneNumber={formData.fallbackDestinationPhoneNumber}
                          onCountryChange={handleChange}
                          onPhoneChange={handleChange}
                        />
                        {formError?.fallbackDestinationPhoneNumberError ? (
                          <div className="form__error">
                            {formError?.fallbackDestinationPhoneNumberError}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="createAssistant">
          <div className="createAssistant__card">
            <div className="createAssistant__details">
              <h2 className="title">Import Phone Number</h2>
              <div className="icon">
                <CallMobileIcon />
              </div>
              <p className="para">
                Assistants are able to be connected to phone numbers for calls.
                You can import from Twilio, vonage, or create a free number
                directly from Vapi for use with your assistants.
              </p>
              <div className="btn__group">
                <button
                  className="btn btn__primary"
                  onClick={() => setImportModalVisible(true)}
                >
                  <AddIcon />
                  Import Number
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPhoneNumber;
